import React, { useState, useEffect } from 'react';
import {
  Container, Card, CardContent, Typography, Button, Box,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, Tab, IconButton, Paper,
} from '@mui/material';
import RefreshIcon   from '@mui/icons-material/Refresh';
import DownloadIcon  from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import * as XLSX from 'xlsx';
import { API_URL } from '../../config';
import WhatsAppSendButton from '../common/WhatsAppSendButton';

const GREEN  = '#1b4332';
const ORANGE = '#f59e0b';

const HDR_SX = {
  fontWeight: 700, color: '#fff', fontSize: '0.70rem', textTransform: 'uppercase',
  background: `linear-gradient(135deg, ${GREEN} 0%, #2d6a4f 100%)`,
  borderRight: '1px solid rgba(255,255,255,0.2)',
  py: 1.0, px: 0.7, whiteSpace: 'nowrap', textAlign: 'center',
};
const CELL_SX = {
  py: 0.8, px: 0.7, fontSize: '0.75rem', textAlign: 'center',
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};

function fmtNum(v, d = 0) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toFixed(d);
}
function fmtDate(val) {
  if (!val) return '—';
  return String(val).substring(0, 10);
}
function isGrandTotal(row) {
  return String(row.MARKET_NAME || '').trim().toUpperCase().startsWith('GRAND TOTAL');
}
function certColor(cert) {
  const c = String(cert || '').toUpperCase();
  if (c.includes('FSC') && !c.includes('NON')) return '#059669';
  if (c.includes('NON')) return '#dc2626';
  return '#6d28d9';
}

function StatChip({ label, value, color = GREEN }) {
  return (
    <Paper elevation={0} sx={{
      px: 2, py: 1.5, borderRadius: 2, textAlign: 'center', minWidth: 120,
      border: `1.5px solid ${color}30`, background: `${color}08`,
    }}>
      <Typography sx={{ fontSize: '0.65rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color }}>{value}</Typography>
    </Paper>
  );
}

// ─── Detail table ──────────────────────────────────────────────────────────────
function DetailTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data</Alert>;
  return (
    <TableContainer sx={{ maxHeight: 580, overflowX: 'auto' }}>
      <Table stickyHeader size="small" sx={{ minWidth: 1300 }}>
        <TableHead>
          <TableRow>
            {['NO', 'PALLET DATE', 'PALLET NO', 'MARKET', 'CERTIFICATE', 'GRADE',
              'SIZE', 'THICKNESS', 'LENGTH', 'WIDTH', 'PCS', 'CBM', 'GLUE TYPE', 'MKT GROUP'
            ].map(h => <TableCell key={h} sx={HDR_SX}>{h}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} sx={{
              backgroundColor: i % 2 === 0 ? '#fff' : '#f0fdf4',
              '&:hover': { backgroundColor: '#d1fae5' },
            }}>
              <TableCell sx={CELL_SX}>{row.SNO || i + 1}</TableCell>
              <TableCell sx={CELL_SX}>{fmtDate(row.PALLET_DATE)}</TableCell>
              <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: GREEN }}>{row.PALLET_NO || '—'}</TableCell>
              <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: '#1e3a5f', textAlign: 'left' }}>{row.MARKET_NAME || '—'}</TableCell>
              <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: certColor(row.CERTIFICATE) }}>{row.CERTIFICATE || '—'}</TableCell>
              <TableCell sx={CELL_SX}>{row.GRADE || '—'}</TableCell>
              <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>{row.SIZE_DISPLAY || '—'}</TableCell>
              <TableCell sx={CELL_SX}>{fmtNum(row.THICKNESS, 2)}</TableCell>
              <TableCell sx={CELL_SX}>{fmtNum(row.LENGTH, 3)}</TableCell>
              <TableCell sx={CELL_SX}>{fmtNum(row.WIDTH, 3)}</TableCell>
              <TableCell sx={{ ...CELL_SX, fontWeight: 700, color: '#166534' }}>{fmtNum(row.PCS, 0)}</TableCell>
              <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: GREEN }}>{fmtNum(row.CBM, 3)}</TableCell>
              <TableCell sx={CELL_SX}>{row.GLUETYPE || '—'}</TableCell>
              <TableCell sx={CELL_SX}>{row.MARKETGROUP || '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Market summary table ──────────────────────────────────────────────────────
function MarketSummaryTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data</Alert>;
  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 500 }}>
        <TableHead>
          <TableRow>
            {['MARKET', 'CERTIFICATE', 'PALLETS', 'PCS', 'CBM'].map(h => (
              <TableCell key={h} sx={HDR_SX}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const grand = isGrandTotal(row);
            return (
              <TableRow key={i} sx={{
                backgroundColor: grand ? '#fef3c7' : (i % 2 === 0 ? '#fff' : '#f0fdf4'),
                '&:hover': { filter: 'brightness(0.97)' },
              }}>
                <TableCell sx={{ ...CELL_SX, fontWeight: grand ? 700 : 600, color: grand ? '#92400e' : '#1e3a5f', textAlign: 'left', minWidth: 160 }}>
                  {grand ? 'GRAND TOTAL' : (row.MARKET_NAME || '—')}
                </TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: grand ? 700 : 500, color: grand ? '#92400e' : certColor(row.CERTIFICATE) }}>
                  {grand ? '' : (row.CERTIFICATE || '—')}
                </TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: grand ? 700 : 600, color: grand ? '#92400e' : '#374151' }}>
                  {fmtNum(row.PALLETS, 0)}
                </TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: grand ? 700 : 600, color: grand ? '#92400e' : '#166534' }}>
                  {fmtNum(row.PCS, 0)}
                </TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: grand ? 700 : 600, color: grand ? '#92400e' : GREEN }}>
                  {fmtNum(row.CBM, 3)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Size + Market summary table ──────────────────────────────────────────────
function SizeSummaryTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data</Alert>;
  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 600 }}>
        <TableHead>
          <TableRow>
            {['MARKET', 'THICKNESS', 'SIZE', 'PALLETS', 'PCS', 'CBM'].map(h => (
              <TableCell key={h} sx={HDR_SX}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} sx={{
              backgroundColor: i % 2 === 0 ? '#fff' : '#ede9fe',
              '&:hover': { filter: 'brightness(0.97)' },
            }}>
              <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: '#1e3a5f', textAlign: 'left', minWidth: 140 }}>
                {row.MARKET_NAME || '—'}
              </TableCell>
              <TableCell sx={CELL_SX}>{fmtNum(row.THICKNESS, 2)}</TableCell>
              <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>{row.SIZE_DISPLAY || '—'}</TableCell>
              <TableCell sx={CELL_SX}>{fmtNum(row.PALLETS, 0)}</TableCell>
              <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: '#166534' }}>{fmtNum(row.PCS, 0)}</TableCell>
              <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: GREEN }}>{fmtNum(row.CBM, 3)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const TAB_LABELS = ['DETAIL', 'MARKET SUMMARY', 'SIZE & MARKET SUMMARY'];

// ─── Main view ────────────────────────────────────────────────────────────────
const PackedPalletStockReportView = ({ onBackClick, user }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [data, setData]       = useState(null);
  const [tab, setTab]         = useState(1); // default to market summary

  const fetchData = async () => {
    setLoading(true); setError(''); setData(null);
    try {
      const res = await fetch(`${API_URL}/api/packed-pallet-stock/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('API error: ' + res.statusText);
      const apiData = await res.json();
      if (!apiData.success || !apiData.data) {
        setError(apiData.message || 'Failed to fetch data.');
        return;
      }
      setData(apiData.data);
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDownload = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    if (data[0]?.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data[0]), 'Detail');
    if (data[1]?.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data[1]), 'Market Summary');
    if (data[2]?.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data[2]), 'Size & Market Summary');
    XLSX.writeFile(wb, `PackedPalletStock_${new Date().toISOString().substring(0, 10)}.xlsx`);
  };

  // Quick stats from grand total row in market summary
  const grandRow     = data?.[1]?.find(r => isGrandTotal(r));
  const totalPallets = grandRow ? fmtNum(grandRow.PALLETS, 0) : '-';
  const totalPCS     = grandRow ? fmtNum(grandRow.PCS, 0)     : '-';
  const totalCBM     = grandRow ? fmtNum(grandRow.CBM, 3)     : '-';
  const detailCount  = data?.[0]?.length ?? 0;

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 4, px: { xs: 1, md: 2 } }}>

      {/* Header card */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(27,67,50,0.1)', border: '1px solid #d1fae5' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: GREEN }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box sx={{ background: `linear-gradient(135deg, ${GREEN} 0%, #2d6a4f 100%)`, borderRadius: 2, p: 1.5, mr: 2 }}>
              <Inventory2Icon sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Box flex={1}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: GREEN }}>
                Packed Pallet Stock Report
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Current unsold packed pallet inventory — market &amp; size wise
              </Typography>
            </Box>
            <Button
              variant="outlined" size="medium" startIcon={<RefreshIcon />}
              onClick={fetchData} disabled={loading}
              sx={{ color: GREEN, borderColor: '#d1fae5', textTransform: 'none', fontWeight: 600,
                '&:hover': { borderColor: GREEN, backgroundColor: '#f0fdf4' } }}
            >
              Refresh
            </Button>
          </Box>

          {data && (
            <Box display="flex" flexWrap="wrap" gap={1.5} mt={1}>
              <StatChip label="Total Pallets" value={totalPallets} />
              <StatChip label="Total PCS"     value={totalPCS}     color={GREEN} />
              <StatChip label="Total CBM"     value={totalCBM}     color={GREEN} />
              <StatChip label="Detail Rows"   value={detailCount}  color={ORANGE} />
            </Box>
          )}
        </CardContent>
      </Card>

      {loading && <Box textAlign="center" py={6}><CircularProgress sx={{ color: GREEN }} /></Box>}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #d1fae5' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 1 }}>
            <Tabs
              value={tab} onChange={(_, v) => setTab(v)}
              textColor="inherit" variant="scrollable" scrollButtons="auto"
              sx={{
                '& .MuiTab-root': { fontWeight: 600, fontSize: '0.78rem' },
                '& .Mui-selected': { color: GREEN },
                '& .MuiTabs-indicator': { backgroundColor: GREEN },
              }}
            >
              {TAB_LABELS.map((l, i) => (
                <Tab key={i} label={i === 0 ? `${l} (${detailCount})` : l} />
              ))}
            </Tabs>
            <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownload}
              sx={{ color: GREEN, fontWeight: 600, textTransform: 'none', ml: 1, flexShrink: 0 }}>
              Excel
            </Button>
          </Box>

          {tab === 0 && <DetailTable        rows={data[0]} />}
          {tab === 1 && <MarketSummaryTable rows={data[1]} />}
          {tab === 2 && <SizeSummaryTable   rows={data[2]} />}
        </Card>
      )}

      {!data && !loading && !error && (
        <Card sx={{ p: 8, textAlign: 'center', borderRadius: 3, background: '#fafafa', boxShadow: 'none', border: '2px dashed #e0e0e0' }}>
          <Inventory2Icon sx={{ fontSize: 60, color: '#bdbdbd', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#666', fontWeight: 500 }}>Loading packed pallet stock…</Typography>
        </Card>
      )}

      {data && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
          <WhatsAppSendButton
            user={user}
            label="Send Packed Pallet Stock to WhatsApp"
            apiUrl={`${API_URL}/api/packed-pallet-stock/send`}
            payload={{}}
          />
        </Box>
      )}
    </Container>
  );
};

export default PackedPalletStockReportView;
